import { VariableOption } from './types';

const coloradoOralCannabinoids = [
  ['cbd', 'CBD'], ['activeThc', 'Active THC'], ['activeCbd', 'Active CBD'], ['cbn', 'CBN'], ['cbda', 'CBDA'],
  ['delta10thc', 'Delta-10 THC'], ['delta8thc', 'Delta-8 THC'], ['delta9thc', 'Delta-9 THC'],
  ['delta9thca', 'Delta-9 THCA'], ['exothc', 'exo-THC'],
] as const;

/** Reported Colorado measurements; per unit is never per package or per serving. */
export const coloradoPotencyVariables: VariableOption[] = [...coloradoOralCannabinoids.flatMap(([key, label]) => {
  const contexts = key === 'activeThc' || key === 'activeCbd' || key === 'exothc'
    ? [['srv', 'serving'], ['perUnit', 'unit']] as const
    : [['perUnit', 'unit']] as const;
  return contexts.map(([field, context]) => ({
    key: `coa.ingredients.${key}.weight.${field}`,
    displayName: `${label} per ${context}`,
    metrcName: 'LabTests (reported Colorado potency)',
    type: 'number' as const,
    description: `Reported ${label} per ${context} in the saved unit; no calculation or substitution`,
    example: '0',
    defaultText: `${label} per ${context}: {{coa.ingredients.${key}.weight.${field}}} {{coa.ingredients.${key}.weight.${field}Uom}}`,
  }));
}), {
  key: 'coa.ingredients.exothc.percent', displayName: 'exo-THC percent',
  metrcName: 'LabTests (reported Colorado potency)', type: 'number',
  description: 'Reported exo-THC percentage; no calculation or substitution', example: '0',
  defaultText: 'exo-THC: {{coa.ingredients.exothc.percent}}%',
}];
const coloradoPotencyKeys = new Set(coloradoPotencyVariables.map(variable => variable.key));
const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};

/** Scope picker choices only. Saved templates can always resolve their original data paths. */
export function labelVariablesForContext(context: { marketCodes?: unknown[]; coa?: unknown }): VariableOption[] {
  const colorado = [...(context.marketCodes ?? []), asRecord(context.coa).stateCode]
    .some(code => typeof code === 'string' && code.trim().toUpperCase() === 'CO');
  const ingredients = asRecord(asRecord(context.coa).ingredients);
  return labelVariables.filter(variable => {
    if (!coloradoPotencyKeys.has(variable.key) || colorado) return true;
    const [, , key, measurement, field] = variable.key.split('.');
    if (measurement === 'percent') return asRecord(ingredients[key]).percent != null;
    const weight = asRecord(asRecord(ingredients[key]).weight);
    return weight[field] !== null && weight[field] !== undefined;
  }).map(variable => {
    const [, , key, measurement, field] = variable.key.split('.');
    const oral = coloradoOralCannabinoids.find(([ingredient]) => ingredient === key);
    if (colorado && oral && measurement === 'weight' && field === 'srv') {
      return { ...variable, defaultText: `${oral[1]} per serving: {{${variable.key}}} {{${variable.key}Uom}}` };
    }
    return variable;
  });
}

export const labelVariables: VariableOption[] = [
  ...coloradoPotencyVariables,
  {
    key: 'labelIndex',
    metrcName: 'N/A (system)',
    type: 'number',
    description: 'Index of the current label in the print job (0-based)',
    example: '0',
  },
  {
    key: 'coa.id',
    metrcName: 'Label',
    type: 'string',
    description: 'Metrc package label/tag identifier',
    example: '1A4060300000001000000123',
  },
  {
    key: 'coa.lotNumber',
    metrcName: 'N/A',
    type: 'string',
    description: 'Lot number for the product',
    example: '',
  },
  {
    key: 'coa.productionBatchDate',
    metrcName: 'N/A (explicit value)',
    type: 'string',
    displayName: 'Production Batch date',
    description: 'Explicit production batch date, independent of Harvest Date',
    example: '',
    defaultText: 'Production Batch date: {{coa.productionBatchDate}}',
  },
  {
    key: 'coa.batch',
    metrcName: 'ProductionBatchNumber / SourceProductionBatchNumbers',
    type: 'string',
    description: 'Production batch number or first source batch number',
    example: 'BATCH-2023-001',
  },
  {
    key: 'coa.sourceBatch',
    metrcName: 'SourceProductionBatchNumbers',
    type: 'string',
    description: 'Source production batch numbers',
    example: 'BATCH-2023-001',
  },
  {
    key: 'coa.batchTag',
    metrcName: 'TestSourcePackageLabel',
    type: 'string',
    description: 'Source package label used for testing',
    example: '1A4060300000001000000456',
  },
  {
    key: 'coa.sourcePackage',
    metrcName: 'SourcePackageLabels',
    type: 'string',
    description: 'Source package labels',
    example: '1A4060300000001000000789',
  },
  {
    key: 'coa.category',
    metrcName: 'ProductCategoryType / ProductCategoryName',
    type: 'string',
    description: 'Product category type or name',
    example: 'Flower',
  },
  {
    key: 'coa.strainName',
    metrcName: 'StrainName',
    type: 'string',
    description: 'Strain name of the product',
    example: 'Blue Dream',
  },
  {
    key: 'coa.productName',
    metrcName: 'Name / ProductName',
    type: 'string',
    description: 'Product name',
    example: 'Blue Dream Flower',
  },
  {
    key: 'coa.isProductionBatch',
    metrcName: 'IsProductionBatch',
    type: 'boolean',
    description: 'Indicates if this is a production batch',
    example: 'TRUE',
  },
  {
    key: 'coa.isTestingSample',
    metrcName: 'IsTestingSample',
    type: 'boolean',
    description: 'Indicates if this is a testing sample',
    example: 'FALSE',
  },
  {
    key: 'coa.quantityType',
    metrcName: 'QuantityType',
    type: 'string',
    description: 'Type of quantity measurement',
    example: 'WeightBased',
  },
  {
    key: 'coa.servingSize',
    metrcName: 'ServingSize',
    type: 'number',
    description: 'Size of a single serving',
    example: '',
  },
  {
    key: 'coa.amountOptions',
    metrcName: 'N/A',
    type: 'string',
    description: "Either 'Both' if weight data exists or undefined",
    example: 'Both',
  },
  {
    key: 'coa.unit.numberOfDoses',
    metrcName: 'NumberOfDoses',
    type: 'number',
    description: 'Number of doses in the package',
    example: '10',
  },
  {
    key: 'coa.unit.cbd.content',
    metrcName: 'UnitCbdContent',
    type: 'number',
    description: 'CBD content per unit',
    example: '5.5',
  },
  {
    key: 'coa.unit.cbd.contentDose',
    metrcName: 'UnitCbdContentDose',
    type: 'number',
    description: 'CBD content per dose',
    example: '0.55',
  },
  {
    key: 'coa.unit.cbd.contentDoseUnitOfMeasureName',
    metrcName: 'UnitCbdContentDoseUnitOfMeasureName',
    type: 'string',
    description: 'Unit of measure for CBD dose',
    example: 'Milligrams',
  },
  {
    key: 'coa.unit.cbd.contentUnitOfMeasureName',
    metrcName: 'UnitCbdContentUnitOfMeasureName',
    type: 'string',
    description: 'Unit of measure for CBD content',
    example: 'Milligrams',
  },
  {
    key: 'coa.unit.cbd.percent',
    metrcName: 'UnitCbdPercent',
    type: 'number',
    description: 'CBD percentage',
    example: '0.55',
  },
  {
    key: 'coa.unit.quantity',
    metrcName: 'UnitQuantity',
    type: 'number',
    description: 'Quantity per unit',
    example: '1',
  },
  {
    key: 'coa.unit.quantityUnitOfMeasureName',
    metrcName: 'UnitQuantityUnitOfMeasureName',
    type: 'string',
    description: 'Unit of measure for quantity',
    example: 'Ounces',
  },
  {
    key: 'coa.unit.thc.content',
    metrcName: 'UnitThcContent',
    type: 'number',
    description: 'THC content per unit',
    example: '25.5',
  },
  {
    key: 'coa.unit.thc.contentDose',
    metrcName: 'UnitThcContentDose',
    type: 'number',
    description: 'THC content per dose',
    example: '2.55',
  },
  {
    key: 'coa.unit.thc.contentDoseUnitOfMeasureName',
    metrcName: 'UnitThcContentDoseUnitOfMeasureName',
    type: 'string',
    description: 'Unit of measure for THC dose',
    example: 'Milligrams',
  },
  {
    key: 'coa.unit.thc.contentUnitOfMeasureName',
    metrcName: 'UnitThcContentUnitOfMeasureName',
    type: 'string',
    description: 'Unit of measure for THC content',
    example: 'Milligrams',
  },
  {
    key: 'coa.unit.thc.percent',
    metrcName: 'UnitThcPercent',
    type: 'number',
    description: 'THC percentage',
    example: '2.55',
  },
  {
    key: 'coa.unit.volume',
    metrcName: 'UnitVolume',
    type: 'number',
    description: 'Volume per unit',
    example: '1',
  },
  {
    key: 'coa.unit.volumeUnitOfMeasureName',
    metrcName: 'UnitVolumeUnitOfMeasureName',
    type: 'string',
    description: 'Unit of measure for volume',
    example: 'Fluid Ounces',
  },
  {
    key: 'coa.unit.weight',
    metrcName: 'UnitWeight',
    type: 'number',
    description: 'Weight per unit',
    example: '3.5',
  },
  {
    key: 'coa.unit.weightUnitOfMeasureName',
    metrcName: 'UnitWeightUnitOfMeasureName',
    type: 'string',
    description: 'Unit of measure for weight',
    example: 'Grams',
  },
  {
    key: 'coa.lab.licenseNumber',
    metrcName: 'LabFacilityLicenseNumber',
    type: 'string',
    description: 'License number of the testing lab',
    example: 'LAB-12345',
  },
  {
    key: 'coa.lab.name',
    metrcName: 'LabFacilityName',
    type: 'string',
    description: 'Name of the testing lab',
    example: 'Testing Lab Inc',
  },
  {
    key: 'coa.lab.docId',
    metrcName: 'LabTestResultDocumentFileId',
    type: 'number',
    description: 'Document file ID for lab test results',
    example: '123456',
  },
  {
    key: 'coa.labTests',
    metrcName: 'LabTests (processed)',
    type: 'array',
    description: 'Array of lab test information with docId and licenseNumber',
    example: "[{docId: 123, licenseNumber: 'LAB-001', at: '2023-01-01'}]",
  },
  {
    key: 'coa.manufacturer.name',
    metrcName: 'ItemFromFacilityName',
    type: 'string',
    description: 'Name of the manufacturer facility',
    example: 'Cultivation Co',
  },
  {
    key: 'coa.manufacturer.licenseNumber',
    metrcName: 'ItemFromFacilityLicenseNumber',
    type: 'string',
    description: 'License number of the manufacturer',
    example: 'LIC-67890',
  },
  {
    key: 'coa.licenseNumber',
    metrcName: 'ItemFromFacilityLicenseNumber',
    type: 'string',
    description: 'License number (duplicate of manufacturer)',
    example: 'LIC-67890',
  },
  {
    key: 'coa.harvestDate',
    metrcName: 'HarvestDate',
    type: 'string',
    description: 'Date the product was harvested',
    example: '1/15/2023',
    defaultText: 'Harvest Date: {{coa.harvestDate}}',
    dateKind: 'harvestDate',
  },
  {
    key: 'coa.dateTested',
    metrcName: 'TestPerformedDate',
    type: 'string',
    description: 'Date the lab test was performed',
    example: '1/20/2023',
  },
  {
    key: 'coa.description',
    metrcName: 'Description',
    type: 'string',
    description: 'Product description',
    example: 'Premium indoor flower',
  },
  {
    key: 'coa.nonCannabinoids',
    metrcName: 'PublicIngredients',
    type: 'string',
    description: 'Non-cannabinoid ingredients',
    example: '',
  },
  {
    key: 'coa.packagedDate',
    metrcName: 'PackagedDate',
    type: 'string',
    description: 'Date the product was packaged',
    example: '1/15/2023',
    defaultText: 'Packaged Date: {{coa.packagedDate}}',
    dateKind: 'packagedDate',
  },
  {
    key: 'coa.manufacturedDate',
    metrcName: 'ManufacturedDate',
    type: 'string',
    description: 'Date the product was manufactured',
    example: '1/10/2023',
  },
  {
    key: 'coa.expirationDate',
    displayName: 'Expiration Date',
    metrcName: 'UseByDate / SellByDate / ExpirationDate',
    type: 'string',
    description: 'Product expiration date (or calculated)',
    example: '1/15/2024',
    defaultText: 'Expiration Date: {{coa.expirationDate}}',
    dateKind: 'expirationDate',
  },
  {
    key: 'coa.sellByDate',
    displayName: 'Sell By Date',
    metrcName: 'SellByDate',
    type: 'string',
    description: 'Date by which the product should be sold',
    example: '1/15/2024',
    defaultText: 'Sell By Date: {{coa.sellByDate}}',
    dateKind: 'sellByDate',
  },
  {
    key: 'coa.shelfLifeDate',
    metrcName: 'Shelflifedate / ShelfLifeDate',
    type: 'string',
    description: 'Shelf life date supplied for the package',
    example: '1/15/2024',
    defaultText: 'Shelf Life Date: {{coa.shelfLifeDate}}',
    dateKind: 'shelfLifeDate',
  },
  {
    key: 'coa.bestByDate',
    metrcName: 'UseByDate / SellByDate / ExpirationDate',
    type: 'string',
    description: 'Best by date supplied or calculated from shelf life',
    example: '1/15/2024',
    defaultText: 'Best By Date: {{coa.bestByDate}}',
    dateKind: 'bestByDate',
  },
  {
    key: 'coa.cultivationDate',
    metrcName: 'CultivationDate',
    type: 'string',
    description: 'Cultivation date supplied for the package',
    example: '1/15/2024',
    defaultText: 'Cultivation Date: {{coa.cultivationDate}}',
    dateKind: 'cultivationDate',
  },
  {
    key: 'coa.useByDate',
    displayName: 'Use By Date',
    metrcName: 'UseByDate',
    type: 'string',
    description: 'Product use by date',
    example: '1/15/2024',
    defaultText: 'Use By Date: {{coa.useByDate}}',
    dateKind: 'useByDate',
  },
  {
    key: 'coa.uom',
    metrcName: 'UnitOfMeasureAbbreviation',
    type: 'string',
    description: 'Unit of measure abbreviation',
    example: 'g',
  },
  {
    key: 'coa.stateCode',
    metrcName: 'stateCode',
    type: 'string',
    description: 'State code where product is registered',
    example: 'CA',
  },
  {
    key: 'coa.metrcMetadata.packageId',
    metrcName: 'Id',
    type: 'number',
    description: 'Metrc package ID',
    example: '987654',
  },
  {
    key: 'coa.metrcMetadata.itemId',
    metrcName: 'Item.Id',
    type: 'number',
    description: 'Metrc item ID',
    example: '456789',
  },
  {
    key: 'coa.ingredients',
    metrcName: 'LabTests (processed)',
    type: 'object',
    description: 'Cannabinoid test results as key-value pairs',
    example:
      "{delta9thc: {percent: 25.5, weight: {amount: 255, srv: 2.55, pkg: 25.5, unit: 'mg'}}}",
  },
  {
    key: 'coa.ingredients.cbc.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBC weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.cbc.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBC package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.cbc.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBC serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbc.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBC percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbd.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBD weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.cbd.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBD package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.cbd.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBD serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbd.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBD percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbda.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBDA weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.cbda.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBDA package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.cbda.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBDA serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbda.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBDA percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbca.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBCA weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.cbca.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBCA package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.cbca.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBCA serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbca.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBCA percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbdv.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBDV weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.cbdv.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBDV package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.cbdv.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBDV serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbdv.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBDV percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbdva.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBDVA weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.cbdva.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBDVA package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.cbdva.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBDVA serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbdva.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBDVA percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbg.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBG weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.cbg.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBG package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.cbg.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBG serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbg.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBG percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbga.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBGA weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.cbga.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBGA package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.cbga.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBGA serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbga.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBGA percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbn.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBN weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.cbn.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBN package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.cbn.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBN serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbn.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBN percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbna.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBNA weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.cbna.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBNA package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.cbna.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBNA serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.cbna.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'CBNA percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.thca.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'THCA weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.thca.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'THCA package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.thca.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'THCA serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.thca.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'THCA percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.delta9thc.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-9 THC weight amount',
    example: '255',
  },
  {
    key: 'coa.ingredients.delta9thc.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-9 THC package weight',
    example: '25.5',
  },
  {
    key: 'coa.ingredients.delta9thc.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-9 THC serving weight',
    example: '2.55',
  },
  {
    key: 'coa.ingredients.delta9thc.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-9 THC percentage',
    example: '25.5',
  },
  {
    key: 'coa.ingredients.delta9thca.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-9 THCA weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.delta9thca.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-9 THCA package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.delta9thca.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-9 THCA serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.delta9thca.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-9 THCA percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.delta8thc.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-8 THC weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.delta8thc.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-8 THC package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.delta8thc.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-8 THC serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.delta8thc.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-8 THC percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.delta8thca.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-8 THCA weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.delta8thca.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-8 THCA package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.delta8thca.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-8 THCA serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.delta8thca.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-8 THCA percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.delta10thc.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-10 THC weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.delta10thc.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-10 THC package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.delta10thc.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-10 THC serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.delta10thc.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Delta-10 THC percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.thcv.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'THCV weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.thcv.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'THCV package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.thcv.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'THCV serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.thcv.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'THCV percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.thcva.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'THCVA weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.thcva.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'THCVA package weight',
    example: '25',
  },
  {
    key: 'coa.ingredients.thcva.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'THCVA serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.thcva.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'THCVA percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.totalCannabinoids.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total cannabinoids weight amount',
    example: '300',
  },
  {
    key: 'coa.ingredients.totalCannabinoids.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total cannabinoids package weight',
    example: '30',
  },
  {
    key: 'coa.ingredients.totalCannabinoids.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total cannabinoids serving weight',
    example: '3',
  },
  {
    key: 'coa.ingredients.totalCannabinoids.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total cannabinoids percentage',
    example: '30',
  },
  {
    key: 'coa.ingredients.totalActiveCannabinoids.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total active cannabinoids (TAC) percentage. Can be added without COA data; the value remains unavailable until reported or calculated.',
    example: '30',
  },
  {
    key: 'coa.ingredients.totalActiveCannabinoids.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total active cannabinoids weight amount',
    example: '368',
  },
  {
    key: 'coa.ingredients.totalActiveCannabinoids.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total active cannabinoids package weight',
    example: '368',
  },
  {
    key: 'coa.ingredients.totalActiveCannabinoids.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total active cannabinoids serving weight',
    example: '36.8',
  },
  {
    key: 'coa.ingredients.totalCbd.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total CBD weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.totalCbd.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total CBD serving weight',
    example: '0.05',
  },
  {
    key: 'coa.ingredients.totalCbd.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total CBD package weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.totalCbd.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total CBD percentage',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.totalThc.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total THC weight amount',
    example: '255',
  },
  {
    key: 'coa.ingredients.totalThc.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total THC serving weight',
    example: '2.55',
  },
  {
    key: 'coa.ingredients.totalThc.weight.pkg',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total THC package weight',
    example: '25.5',
  },
  {
    key: 'coa.ingredients.totalThc.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total THC percentage',
    example: '25.5',
  },
  {
    key: 'coa.ingredients.totalTerpenes.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Total terpenes percentage',
    example: '2.5',
  },
  {
    key: 'coa.ingredients.otherTerpenes.weight.amount',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Other terpenes weight amount',
    example: '5',
  },
  {
    key: 'coa.ingredients.otherTerpenes.weight.srv',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Other terpenes serving weight',
    example: '0.5',
  },
  {
    key: 'coa.ingredients.otherTerpenes.percent',
    metrcName: 'LabTests (processed)',
    type: 'number',
    description: 'Other terpenes percentage',
    example: '0.5',
  },
  {
    key: 'coa.totals.terpenes',
    metrcName: 'N/A (calculated)',
    type: 'object',
    description: 'Total terpenes ingredient value',
    example: '{percent: 2.5}',
  },
  {
    key: 'coa.totals.cbd',
    metrcName: 'N/A (calculated)',
    type: 'object',
    description: 'Total CBD ingredient value',
    example: "{percent: 0.5, weight: {amount: 5, srv: 0.05, pkg: 0.5, unit: 'mg'}}",
  },
  {
    key: 'coa.totals.thc',
    metrcName: 'N/A (calculated)',
    type: 'object',
    description: 'Total THC ingredient value',
    example: "{percent: 25.5, weight: {amount: 255, srv: 2.55, pkg: 25.5, unit: 'mg'}}",
  },
  {
    key: 'coa.totals.cannabinoids',
    metrcName: 'N/A (calculated)',
    type: 'object',
    description: 'Total cannabinoids ingredient value',
    example: "{percent: 30, weight: {amount: 300, srv: 3, pkg: 30, unit: 'mg'}}",
  },
  {
    key: 'coa.terpenes',
    metrcName: 'LabTests (processed)',
    type: 'object',
    description: 'Terpene test results as key-value pairs',
    example: "{limonene: {percent: 0.5, label: 'Limonene'}}",
  },
  {
    key: 'metrc.terpenes',
    metrcName: 'Metrc terpenes metadata',
    type: 'object',
    description: 'Terpene metadata map with labels',
    example: "{limonene: {label: 'Limonene'}}",
  },
  {
    key: 'topTerpenes',
    metrcName: 'N/A (computed)',
    type: 'string',
    description:
      'Top terpenes sorted by %. Use {{topTerpenes.0}} {{topTerpenes.1}} {{topTerpenes.2}} etc.',
    example: 'β-Myrcene: 2.45%',
    defaultText: 'Top Terpenes: {{topTerpenes.0}} {{topTerpenes.1}} {{topTerpenes.2}}',
  },
  {
    key: 'topTerpenes.0',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top terpene #1',
    example: 'β-Myrcene: 2.45%',
  },
  {
    key: 'topTerpenes.0.label',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top terpene #1 name',
    example: 'β-Myrcene',
  },
  {
    key: 'topTerpenes.0.value',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top terpene #1 percentage',
    example: '2.45%',
  },
  {
    key: 'topTerpenes.1',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top terpene #2',
    example: 'Limonene: 1.82%',
  },
  {
    key: 'topTerpenes.1.label',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top terpene #2 name',
    example: 'Limonene',
  },
  {
    key: 'topTerpenes.1.value',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top terpene #2 percentage',
    example: '1.82%',
  },
  {
    key: 'topTerpenes.2',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top terpene #3',
    example: 'β-Caryophyllene: 1.15%',
  },
  {
    key: 'topTerpenes.2.label',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top terpene #3 name',
    example: 'β-Caryophyllene',
  },
  {
    key: 'topTerpenes.2.value',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top terpene #3 percentage',
    example: '1.15%',
  },
  {
    key: 'topCannabinoids',
    metrcName: 'N/A (computed)',
    type: 'string',
    description:
      'Top cannabinoids sorted by %. Use {{topCannabinoids.0}} {{topCannabinoids.1}} {{topCannabinoids.2}} etc.',
    example: 'Δ9 THC: 25.50%',
    defaultText:
      'Top Cannabinoids: {{topCannabinoids.0}} {{topCannabinoids.1}} {{topCannabinoids.2}}',
  },
  {
    key: 'topCannabinoids.0',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top cannabinoid #1',
    example: 'Δ9 THC: 25.50%',
  },
  {
    key: 'topCannabinoids.0.label',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top cannabinoid #1 name',
    example: 'Δ9 THC',
  },
  {
    key: 'topCannabinoids.0.value',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top cannabinoid #1 percentage',
    example: '25.50%',
  },
  {
    key: 'topCannabinoids.1',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top cannabinoid #2',
    example: 'THCa: 2.30%',
  },
  {
    key: 'topCannabinoids.1.label',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top cannabinoid #2 name',
    example: 'THCa',
  },
  {
    key: 'topCannabinoids.1.value',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top cannabinoid #2 percentage',
    example: '2.30%',
  },
  {
    key: 'topCannabinoids.2',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top cannabinoid #3',
    example: 'CBD: 0.50%',
  },
  {
    key: 'topCannabinoids.2.label',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top cannabinoid #3 name',
    example: 'CBD',
  },
  {
    key: 'topCannabinoids.2.value',
    metrcName: 'N/A (computed)',
    type: 'string',
    description: 'Top cannabinoid #3 percentage',
    example: '0.50%',
  },
  {
    key: 'iid',
    metrcName: 'N/A (derived from Label)',
    type: 'number',
    description: '',
    example: '',
  },
  {
    key: 'job.packagedDate',
    metrcName: 'Job PackagedDate',
    type: 'string',
    description: 'Packaged date supplied by the job',
    example: '1/15/2023',
  },
  {
    key: 'job.packageTag',
    metrcName: 'Job PackageTag',
    type: 'string',
    description: 'Package tag supplied by the job',
    example: '1A4060300000001000000123',
  },
  {
    key: 'product.title',
    metrcName: 'Name / ProductName',
    type: 'string',
    description: 'Product title or name',
    example: 'Test OG',
  },
  {
    key: 'product.strain',
    metrcName: 'StrainName',
    type: 'string',
    description: 'Strain name of the product',
    example: 'Test OG',
  },
  {
    key: 'product.subSpecies',
    metrcName: 'StrainType',
    type: 'string',
    description: 'Product strain type or subspecies',
    example: 'Hybrid',
  },
  {
    key: 'product.category',
    metrcName: 'ProductCategoryType / ProductCategoryName',
    type: 'string',
    description: 'Product category',
    example: 'Preroll 1g',
  },
  {
    key: 'product.manufacturer.data.title',
    metrcName: 'ItemFromFacilityName',
    type: 'string',
    description: 'Manufacturer organization name',
    example: 'Maryland',
  },
  {
    key: 'product.description',
    metrcName: 'Description',
    type: 'string',
    description: 'Product description',
    example: '',
  },
  {
    key: 'product.strainName',
    metrcName: 'StrainName',
    type: 'string',
    description: 'Strain name (alternate field)',
    example: 'Blue Dream',
  },
  {
    key: 'product.upc',
    metrcName: 'ProductUpc',
    type: 'string',
    description: 'UPC value of the product',
    example: '012345678905',
  },
];
